import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus, AlertCircle, Sparkles, Stethoscope, Briefcase, Building2, Lightbulb, Mail, Phone, MessageCircle, Users, Church, HeartHandshake, Check } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import api, { formatApiError } from "@/lib/api";
import PhoneInput from "@/components/PhoneInput";
import OtpModal, { otpApiError } from "@/components/OtpModal";

const ALLOWED_KINDS = ["business", "venture", "professional", "ngo", "church", "community_leader"];
// Values accepted from the URL ?kind= param to pre-select a profile card.
// Includes "community" (the member-only card) which is not a directory kind.
const PRESELECT_KINDS = [...ALLOWED_KINDS, "community"];
const KINDS_WITH_CATEGORY = ["business", "venture", "professional"];

// Profile types the user can pick directly at sign-up. "community" is a
// sentinel meaning "just a member account" (no directory profile).
const PROFILE_OPTIONS = [
  { value: "community", Icon: HeartHandshake, color: "bg-teal-deep" },
  { value: "business", Icon: Briefcase, color: "bg-orange" },
  { value: "venture", Icon: Lightbulb, color: "bg-amber-500" },
  { value: "professional", Icon: Stethoscope, color: "bg-teal-deep" },
  { value: "ngo", Icon: Building2, color: "bg-violet-600" },
  { value: "church", Icon: Church, color: "bg-violet-600" },
  { value: "community_leader", Icon: Users, color: "bg-rose-500" },
];

const KIND_BADGE = {
  business: { Icon: Briefcase, color: "bg-orange" },
  venture: { Icon: Lightbulb, color: "bg-amber-500" },
  professional: { Icon: Stethoscope, color: "bg-teal-deep" },
  ngo: { Icon: Building2, color: "bg-violet-600" },
  church: { Icon: Building2, color: "bg-violet-600" },
  community_leader: { Icon: Building2, color: "bg-rose-500" },
};

const RegisterAccount = () => {
  const { t } = useI18n();
  const { registerAccount } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawKind = searchParams.get("kind") || "";
  const prefKind = PRESELECT_KINDS.includes(rawKind) ? rawKind : "";
  const refSlug = (searchParams.get("ref") || "").toLowerCase().trim();

  // User picks the profile type in-form. Pre-select from the URL ?kind= if present.
  const [selectedKind, setSelectedKind] = useState(prefKind || null);
  const isDirectoryKind = !!selectedKind && selectedKind !== "community";

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    password2: "",
    city: "",
    source: refSlug,
    business_name: "",
    category: "",
    whatsapp: "",
  });
  const [mode, setMode] = useState("email");
  const [phone, setPhone] = useState({ dialCode: "+1", number: "" });
  const [otpOpen, setOtpOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState(() => Object.keys(t.categories || {}));
  const fullPhone = `${phone.dialCode}${phone.number}`;
  const requiresCategory = KINDS_WITH_CATEGORY.includes(selectedKind);

  useEffect(() => {
    if (requiresCategory) {
      api.get("/categories")
        .then(({ data }) => { if (Array.isArray(data.categories) && data.categories.length) setCategories(data.categories); })
        .catch(() => {});
    }
  }, [requiresCategory]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const doRegister = async () => {
    setLoading(true);
    try {
      const { password2, email, business_name, category, whatsapp, ...rest } = form;
      void password2;
      const base = mode === "email" ? { ...rest, email } : { ...rest, phone: fullPhone };
      // If a directory kind is selected, include the profile fields so the
      // backend creates user + profile atomically (single registration step).
      const payload = isDirectoryKind
        ? { ...base, kind: selectedKind, business_name, category, whatsapp }
        : base;
      const res = await registerAccount(payload);
      const savedProfile = res?.profile;
      if (savedProfile?.id) {
        if (selectedKind === "professional") {
          navigate(`/profiles/${savedProfile.id}/agenda?welcome=1`);
        } else if (selectedKind === "business" || selectedKind === "venture") {
          navigate(`/profiles/${savedProfile.id}/catalog?welcome=1`);
        } else {
          navigate("/my-profiles?welcome=1");
        }
      } else if (isDirectoryKind) {
        // Fallback: profile creation failed, take them to the wizard
        navigate(`/profiles/new?welcome=1&kind=${selectedKind}`);
      } else {
        // Community member — no directory profile
        navigate("/directory?welcome=1");
      }
    } catch (err) {
      const mapped = otpApiError(err, t);
      setError(mapped === t.otp.sendFailed ? formatApiError(err) : mapped);
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!selectedKind) {
      setError(t.registerAccount.pickRequired);
      return;
    }
    if (form.password !== form.password2) {
      setError(t.fields.passwordsDoNotMatch);
      return;
    }
    if (mode === "phone") { setOtpOpen(true); return; }
    await doRegister();
  };

  return (
    <section className="section bg-cream min-h-[80vh]">
      <div className="container-tight max-w-xl">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 bg-orange/10 text-orange text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
            <Sparkles size={13} /> {t.registerAccount.eyebrow}
          </span>
          {isDirectoryKind && KIND_BADGE[selectedKind] && (
            <div className={`mt-4 inline-flex items-center gap-2 ${KIND_BADGE[selectedKind].color} text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full`} data-testid="register-kind-hint">
              {React.createElement(KIND_BADGE[selectedKind].Icon, { size: 14 })}
              {t.registerAccount[`kindHint_${selectedKind}`] || `Registro como ${selectedKind}`}
            </div>
          )}
          <h1 className="font-display text-4xl md:text-5xl text-teal-deep leading-tight mt-4">
            {t.registerAccount.title}
          </h1>
          <p className="text-teal-soft mt-3 max-w-md mx-auto">{t.registerAccount.subtitle}</p>
        </div>

        <form className="mt-10 bg-white rounded-2xl border border-gray-200 p-6 md:p-8" onSubmit={submit}>
          {error && (
            <p className="mb-4 text-red-600 text-sm inline-flex items-center gap-2" data-testid="reg-account-error">
              <AlertCircle size={14} /> {error}
            </p>
          )}

          {refSlug && (
            <div className="mb-4 bg-orange/10 border border-orange/30 rounded-xl p-3 text-sm text-teal-deep inline-flex items-center gap-2" data-testid="reg-ref-banner">
              <Sparkles size={14} className="text-orange shrink-0" />
              <span>Te estás registrando a través de la campaña <b>{refSlug}</b>. ¡Gracias!</span>
            </div>
          )}

          {/* Profile type picker — single-step registration */}
          <div className="mb-6" data-testid="reg-kind-picker">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-orange mb-1">
              <Sparkles size={13} /> {t.registerAccount.pickTitle}
            </div>
            <p className="text-xs text-gray-400 mb-3">{t.registerAccount.pickHint}</p>
            <div className="grid grid-cols-2 gap-2.5">
              {PROFILE_OPTIONS.map(({ value, Icon, color }) => {
                const active = selectedKind === value;
                return (
                  <button
                    type="button"
                    key={value}
                    onClick={() => setSelectedKind(value)}
                    aria-pressed={active}
                    className={`relative text-left rounded-xl border p-3 transition-all ${active ? "border-orange ring-2 ring-orange/30 bg-orange/5" : "border-gray-200 hover:border-orange/50 bg-white"}`}
                    data-testid={`reg-kind-${value}`}
                  >
                    {active && (
                      <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-orange text-white flex items-center justify-center">
                        <Check size={11} />
                      </span>
                    )}
                    <span className={`w-9 h-9 rounded-lg ${color} text-white flex items-center justify-center`}>
                      <Icon size={17} />
                    </span>
                    <span className="block font-bold text-teal-deep text-sm mt-2">{t.registerAccount.pick[value].label}</span>
                    <span className="block text-xs text-teal-soft leading-snug mt-0.5">{t.registerAccount.pick[value].desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <Field label={t.fields.fullName + " *"}>
            <input
              type="text"
              required
              minLength={2}
              maxLength={120}
              className="field-input"
              value={form.full_name}
              onChange={set("full_name")}
              data-testid="reg-account-fullname"
            />
          </Field>

          <Field label={t.otp.createWith}>
            <div className="grid grid-cols-2 gap-1 bg-cream rounded-xl p-1" data-testid="reg-cred-mode-toggle">
              <button
                type="button" onClick={() => setMode("email")}
                className={`inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${mode === "email" ? "bg-white text-teal-deep shadow-sm" : "text-teal-soft hover:text-teal-deep"}`}
                data-testid="reg-cred-mode-email"
              >
                <Mail size={13} /> {t.otp.emailOpt}
              </button>
              <button
                type="button" onClick={() => setMode("phone")}
                className={`inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${mode === "phone" ? "bg-white text-teal-deep shadow-sm" : "text-teal-soft hover:text-teal-deep"}`}
                data-testid="reg-cred-mode-phone"
              >
                <Phone size={13} /> {t.otp.phoneOpt}
              </button>
            </div>
          </Field>

          {mode === "email" ? (
            <Field label={t.auth.email + " *"}>
              <input
                type="email"
                required
                className="field-input"
                value={form.email}
                onChange={set("email")}
                data-testid="reg-account-email"
              />
            </Field>
          ) : (
            <div className="mb-4">
              <PhoneInput
                label={<>{t.fields.phone} <span className="text-red-500">*</span></>}
                value={phone}
                onChange={setPhone}
              />
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t.fields.createPassword + " *"}>
              <input
                type="password"
                required
                minLength={6}
                className="field-input"
                value={form.password}
                onChange={set("password")}
                data-testid="reg-account-password"
              />
            </Field>
            <Field label={t.fields.confirmPassword + " *"}>
              <input
                type="password"
                required
                minLength={6}
                className="field-input"
                value={form.password2}
                onChange={set("password2")}
                data-testid="reg-account-password2"
              />
            </Field>
          </div>

          <Field label={t.fields.city} hint={t.registerAccount.cityHint}>
            <input
              type="text"
              className="field-input"
              value={form.city}
              onChange={set("city")}
              data-testid="reg-account-city"
            />
          </Field>

          {isDirectoryKind && (
            <div className="mt-6 pt-6 border-t border-gray-200" data-testid="reg-account-profile-block">
              <div className="mb-4 flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-orange">
                <Sparkles size={13} /> Datos de tu perfil
              </div>

              <Field
                label={
                  (selectedKind === "professional" ? "Nombre profesional o consultorio" :
                   selectedKind === "business" ? "Nombre del negocio" :
                   selectedKind === "venture" ? "Nombre del emprendimiento" :
                   selectedKind === "ngo" ? "Nombre de la ONG" :
                   selectedKind === "church" ? "Nombre de la iglesia" :
                   "Nombre de tu organización") + " *"
                }
                hint="Como quieres que aparezca en el directorio"
              >
                <input
                  type="text"
                  required
                  minLength={2}
                  maxLength={120}
                  className="field-input"
                  value={form.business_name}
                  onChange={set("business_name")}
                  data-testid="reg-account-business-name"
                />
              </Field>

              {requiresCategory && (
                <Field label="Categoría *">
                  <select
                    required
                    className="field-input"
                    value={form.category}
                    onChange={set("category")}
                    data-testid="reg-account-category"
                  >
                    <option value="" disabled>
                      {categories.length === 0 ? "Cargando categorías…" : "— Selecciona una categoría —"}
                    </option>
                    {categories.map((c) => (
                      <option key={c} value={c}>{t.categories?.[c] || c}</option>
                    ))}
                  </select>
                </Field>
              )}

              <Field label="WhatsApp (opcional)" hint="Con código de país. Ej: +1 555 123 4567">
                <div className="relative">
                  <MessageCircle size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-teal-soft" />
                  <input
                    type="tel"
                    className="field-input !pl-9"
                    value={form.whatsapp}
                    onChange={set("whatsapp")}
                    placeholder="+15551234567"
                    data-testid="reg-account-whatsapp"
                  />
                </div>
              </Field>
            </div>
          )}

          {!refSlug && (
            <Field label={t.auth.source}>
              <select className="field-input" value={form.source} onChange={set("source")} data-testid="reg-account-source">
                <option value="">—</option>
                {Object.entries(t.auth.sources).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </Field>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-orange w-full justify-center mt-3"
            data-testid="reg-account-submit"
          >
            <UserPlus size={14} />
            {loading ? t.registerAccount.creating : t.registerAccount.submit}
          </button>

          <p className="text-xs text-center text-teal-soft mt-4">
            {isDirectoryKind ? "Al registrarte, tu perfil se crea automáticamente. Podrás editarlo y añadir fotos después." : t.registerAccount.afterHint}
          </p>
        </form>

        <p className="text-center text-sm text-teal-soft mt-6">
          {t.auth.hasAccount}{" "}
          <Link to="/login" className="text-orange font-bold hover:underline">
            {t.auth.loginLink}
          </Link>
        </p>

        <OtpModal
          open={otpOpen}
          phone={fullPhone}
          onClose={() => setOtpOpen(false)}
          onVerified={async () => { setOtpOpen(false); await doRegister(); }}
        />
      </div>
    </section>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="block mb-4">
    <span className="block text-xs uppercase tracking-wider text-teal-soft font-bold mb-1">{label}</span>
    {children}
    {hint && <span className="block text-xs text-gray-400 mt-1">{hint}</span>}
  </label>
);

export default RegisterAccount;
